import { SERVER_URL } from '../DB_Url';

const WAITLIST_TIMEOUT_MS = 10000;

class WaitlistApiError extends Error {
  constructor(message, { status = null, shouldFallback = false, data = null, cause = null } = {}) {
    super(message);
    this.name = 'WaitlistApiError';
    this.status = status;
    this.shouldFallback = shouldFallback;
    this.data = data;
    this.cause = cause;
  }
}

const parseJsonSafely = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return null;
  }

  try {
    return await response.json();
  } catch {
    return null;
  }
};

class WaitlistApi {
  constructor() {
    this.baseUrl = SERVER_URL;
  }

  async request(path, { method = 'POST', body, headers = {} } = {}) {
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), WAITLIST_TIMEOUT_MS);

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });

      const data = await parseJsonSafely(response);

      // 2xx and 3xx are "ok". For waitlist, backend returns 202 for pending
      // and 200 for already-verified — both are success cases for the caller.
      if (!response.ok) {
        throw new WaitlistApiError(
          data?.error || data?.message || `Waitlist request failed with status ${response.status}.`,
          {
            status: response.status,
            shouldFallback: response.status >= 500 || response.status === 429,
            data,
          },
        );
      }

      return data || {};
    } catch (error) {
      if (error instanceof WaitlistApiError) {
        throw error;
      }

      if (error?.name === 'AbortError') {
        throw new WaitlistApiError('Waitlist request timed out.', {
          shouldFallback: true,
          cause: error,
        });
      }

      throw new WaitlistApiError(
        error?.message || 'Unable to reach waitlist service.',
        {
          shouldFallback: true,
          cause: error,
        },
      );
    } finally {
      globalThis.clearTimeout(timeoutId);
    }
  }

  subscribe(email) {
    return this.request('/api/waitlist/subscribe', {
      method: 'POST',
      body: { email },
    });
  }

  confirm(token) {
    return this.request('/api/waitlist/confirm', {
      method: 'POST',
      body: { token },
    });
  }
}

const waitlistApi = new WaitlistApi();

export default waitlistApi;
export { WaitlistApiError };
